import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as ContentSchemaRenderProps } from "~/components/content/content-schema-render";
import { ContentSchemaRender } from "~/components/content/content-schema-render";
import type { InlineAdSlotRenderProps } from "~/components/content/content-schema-render/inline-ad-slot/hooks";
import { useInlineAdSlotRender } from "~/components/content/content-schema-render/inline-ad-slot/hooks";
import { useTrackViewedPercentageRender } from "~/components/content/content-schema-render/track-viewed-percentage/hooks";
import type { contentBodyContent$key } from "~/queries/__generated__/contentBodyContent.graphql";

import { NumberOfReadableElementInPaywall } from "./consts";
import { ContentBodyContainer, InlinePaywallOverlayer } from "./styles";

export type Props = {
  inlineAdSlots?: InlineAdSlotRenderProps;
  isShowInlinePaywall?: boolean;
  preprocessSchema?: (schema: Schema) => Schema;
  reference: contentBodyContent$key;
} & Pick<ContentSchemaRenderProps, "advertising" | "extraRenderMap">;

export const ContentBody: FunctionComponent<Props> = ({
  advertising,
  extraRenderMap = {},
  inlineAdSlots = {},
  isShowInlinePaywall,
  preprocessSchema = schema => schema,
  reference: reference_,
  ...attribs
}) => {
  const content = useFragment(
    graphql`
      fragment contentBodyContent on Content
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        body(customContents: $customContents) {
          json
        }
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const { inlineAdSlotRenders } = useInlineAdSlotRender(inlineAdSlots);
  const { trackViewedPercentageRenders } = useTrackViewedPercentageRender();

  const schema = useMemo(() => {
    const bodyJson = content?.body?.json;
    return bodyJson
      ? preprocessSchema(
          isShowInlinePaywall ? bodyJson.slice(0, NumberOfReadableElementInPaywall) : bodyJson,
        )
      : null;
  }, [content?.body?.json, isShowInlinePaywall, preprocessSchema]);
  if (!content.body?.json) return null;

  return (
    <ContentBodyContainer {...attribs}>
      <ContentSchemaRender
        advertising={advertising}
        extraRenderMap={{
          ...inlineAdSlotRenders,
          ...trackViewedPercentageRenders,
          ...extraRenderMap,
        }}
        reference={content}
        schema={schema}
      />
      {isShowInlinePaywall && <InlinePaywallOverlayer />}
    </ContentBodyContainer>
  );
};

ContentBody.displayName = "ContentBody";
